// src/lib/inboxFolders.js — which messages belong in which folder.
//
// Gmail decides whether a message exists and where it lives; analysis only
// adds to it. Two consequences, both pure rules here:
//
//   1. email_intelligence.status is the ANALYSIS lifecycle plus one mirror of
//      Gmail state: 'archived' means "INBOX label absent" (kept by push and the
//      label reconciliation). It narrows only the Inbox. Starred, Sent, labels
//      and search look across archived mail too, and Archive shows it alone.
//   2. A message Gmail delivered but analysis has not finished is still mail.
//      It is read from saved_emails and shown in its folder (its Gmail labels
//      say which), marked "not analyzed yet" — never hidden until a model ran.
//
// For an unanalyzed row there is no email_intelligence row yet, so (1)'s
// mirror doesn't exist — folder placement has to read saved_emails.labels
// instead, and that array is ambiguous on its own: it tracks only INBOX /
// UNREAD / STARRED / TRASH incrementally, so a message Gmail has genuinely
// archived (none of those apply) ends up with labels = [], the exact same
// value a row saved before labels were ever recorded carries. in_inbox
// (saved_emails.in_inbox — api/_lib/gmail-history-sync.js,
// api/_lib/gmail-label-reconcile.js) is Gmail's confirmed answer, written
// independently of the labels array, and is what breaks the tie: null means
// "never confirmed" (treat as Inbox, same as always), true/false is Gmail's
// own last word regardless of what labels happens to hold.

export const ANALYZED = ['analyzed', 'reviewed']
export const ANALYZED_OR_ARCHIVED = ['analyzed', 'reviewed', 'archived']
// saved_emails.intelligence_status values that mean "delivered, analysis
// not finished". 'external' (iCloud) and 'deleted' are excluded on purpose.
export const UNANALYZED_STATUSES = ['pending', 'analyzing', 'backfill', 'error']

/** The email_intelligence.status values a folder shows. */
export function listStatusesFor(filters = {}) {
  if (filters.status) return [filters.status]
  if (filters.archived === true) return ['archived']
  if (filters.search || filters.is_outbound === true || filters.is_starred === true || filters.label) return ANALYZED_OR_ARCHIVED
  return ANALYZED
}

/** Does a folder view include not-yet-analyzed mail at all? */
export function folderShowsUnanalyzed(filters = {}) {
  if (filters.snoozed || filters.view === 'drafts' || filters.status) return false
  // Search reads the whole saved archive by full text (EmailIntelligenceList),
  // which already includes unanalyzed mail.
  if (filters.search) return false
  // Category and triage tabs are analysis results — a message without
  // analysis has no category yet. It shows in the primary view.
  if (filters.category && filters.category !== 'focused') return false
  if (filters.urgency || filters.email_type || filters.triage_status) return false
  return true
}

const labelsOf = row => (Array.isArray(row?.labels) ? row.labels : [])

/**
 * This row's confirmed INBOX membership, true | false | null. null means
 * "never confirmed" — labels = [] with no in_inbox value at all is the
 * ambiguous legacy case (rows saved before labels were kept); treated as
 * Inbox for inbound mail elsewhere below, so nothing disappears. in_inbox,
 * once set, is authoritative regardless of what labels holds (it exists
 * specifically for the case labels alone can't represent: Gmail confirming
 * none of the tracked labels apply).
 */
function confirmedInInbox(row) {
  if (row?.in_inbox === true) return true
  if (row?.in_inbox === false) return false
  return null
}

/**
 * Does a saved (unanalyzed) message belong in this folder? Pure.
 */
export function unanalyzedInFolder(row, filters = {}) {
  const labels = labelsOf(row)
  const confirmed = confirmedInInbox(row)
  const known = labels.length > 0 || confirmed !== null
  const inInbox = confirmed !== null ? confirmed : labels.includes('INBOX')
  if (labels.includes('TRASH') || labels.includes('SPAM')) return false
  if (filters.unread && row.is_read !== false) return false
  if (filters.is_outbound === true) return !!row.is_outbound
  if (filters.is_starred === true) return labels.includes('STARRED')
  if (filters.archived === true) return known && !inInbox && !row.is_outbound
  if (filters.label) return labels.includes(filters.label)
  if (filters.search) return true
  if (row.is_outbound) return false
  return !known || inInbox
}

/** A saved message, in the list's row shape, marked as not analyzed yet. */
export function toUnanalyzedItem(row) {
  const text = String(row.body_text || '').replace(/\s+/g, ' ').trim()
  return {
    id: row.gmail_message_id,
    gmail_message_id: row.gmail_message_id,
    gmail_thread_id: row.gmail_thread_id || null,
    user_id: row.user_id || null,
    subject: row.subject || '',
    from_name: row.sender_name || row.sender_email || '',
    from_email: row.sender_email || '',
    to_emails: row.to_emails || [],
    cc_emails: row.cc_emails || [],
    sent_at: row.sent_at || null,
    is_read: row.is_read !== false,
    is_outbound: !!row.is_outbound,
    labels: labelsOf(row),
    in_inbox: row.in_inbox === true || row.in_inbox === false ? row.in_inbox : null,
    snippet: text.slice(0, 160),
    _isGmailOnly: true,
    _unanalyzed: true,
    analysis_state: row.intelligence_status || 'pending',
  }
}

/**
 * Merge unanalyzed rows into the analyzed list, newest first. A message that
 * has both (analysis finished between the two reads) keeps its analyzed row.
 */
export function mergeUnanalyzed(items, unanalyzedRows, filters = {}) {
  const have = new Set((items || []).map(i => i.gmail_message_id).filter(Boolean))
  const extra = (unanalyzedRows || [])
    .filter(r => r?.gmail_message_id && !have.has(r.gmail_message_id))
    .filter(r => unanalyzedInFolder(r, filters))
    .map(toUnanalyzedItem)
  if (!extra.length) return items || []
  return [...(items || []), ...extra].sort((a, b) => String(b.sent_at || '').localeCompare(String(a.sent_at || '')))
}
